// Shared deal display bits: stage pills and small deal lists.

import { Link } from 'react-router';
import { date, money } from '../lib/format';
import { DEAL_TYPE_LABEL } from '../lib/deals';
import { Empty, Pill, type PillColor } from './ui';

const STAGE_COLOR: Record<string, PillColor> = {
  search: 'gray',
  bov: 'gray',
  listing: 'blue',
  listing_marketing: 'blue',
  lois: 'indigo',
  lease_negotiation: 'purple',
  contract_negotiation: 'purple',
  lease_signed: 'teal',
  under_contract: 'teal',
  occupancy: 'yellow',
  closed_fully_paid: 'green',
  closed: 'green',
};

export function StagePill({ stage, label }: { stage: string; label?: string }) {
  return <Pill color={STAGE_COLOR[stage] ?? 'gray'}>{label ?? stage.replace(/_/g, ' ')}</Pill>;
}

export const PAYMENT_COLOR: Record<string, PillColor> = { projected: 'gray', invoiced: 'blue', received: 'green', overdue: 'red' };

export interface DealListRow {
  id: string;
  name: string;
  deal_type: string;
  stage: string;
  expected_close_date?: string | null;
  next_step?: string | null;
  total_commission?: number | null;
}

export function DealList({ deals }: { deals: DealListRow[] }) {
  if (deals.length === 0) return <Empty>No deals yet.</Empty>;
  return (
    <ul className="divide-y divide-slate-100">
      {deals.map((d) => (
        <li key={d.id} className="py-2">
          <Link to={`/deals/${d.id}`} className="flex flex-wrap items-center gap-2 hover:text-indigo-700">
            <span className="font-medium text-slate-900">{d.name}</span>
            <Pill>{DEAL_TYPE_LABEL[d.deal_type] ?? d.deal_type}</Pill>
            <StagePill stage={d.stage} />
            {d.total_commission ? <span className="ml-auto text-sm text-slate-600">{money(d.total_commission, false)}</span> : null}
          </Link>
          {(d.next_step || d.expected_close_date) && (
            <div className="mt-0.5 text-xs text-slate-500">
              {d.next_step}
              {d.expected_close_date && ` · closes ${date(d.expected_close_date)}`}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
